/**
 * Finalize Cancellation Modal — "ปิดใบแจ้งยกเลิก" ทันทีเมื่อของกลับครบ (Fix ก, 20 ก.ย. 2026)
 *
 * ทำไมต้องมี (prod): Support แจ้งยกเลิกสัญญา RNT-20260716-0006 (วันยกเลิก 30/09) แล้วรับคืน
 * เครื่อง+ซิมครบทันที — สัญญายัง "รอการยกเลิก" (PENDING_CANCELLATION) ค้างจนถึง 30/09 ระบบจึงนับว่า
 * สัญญานั้นยังถือของอยู่ → ซิมที่คืนแล้วขึ้น "พร้อมใช้งาน" (AVAILABLE) แต่ picker ใบงานเตรียมเครื่อง
 * หาไม่เจอ ("ไม่พบซิมว่างที่ตรงกับ…") — ของอยู่ในมือ Support จริงแต่ระบบยังล็อกไว้
 *
 * กติกา (API_CONTRACT.md ก-1/ก-2): ปิดใบแจ้งยกเลิก ⇒ สัญญาเปลี่ยนเป็น "ยกเลิกแล้ว" ทันที
 * — **ไม่แตะวันคิดเงิน** (ยังคิดถึง billing_end_date ที่แจ้งไว้เดิม) มัดจำที่ชำระแล้วเข้าคิว
 * "รอคืนมัดจำ" (เริ่มนับ SLA 15 วันทำการวันนี้) และของที่รับคืนแล้วใช้งานใหม่ได้ทันที
 *
 * ใช้ร่วมกันทั้งหน้ารายละเอียดการเช่าและหน้าติดตามการคืนอุปกรณ์ (props เป็น "สถานะที่โหลดมาแล้ว"
 * ไม่ใช่ rentalId เดี่ยว ๆ) — caller เป็นคนตัดสินใจว่าจะเปิดโมดัลนี้เมื่อไหร่ (eligible=true)
 */

import React, { useEffect, useState } from 'react';
import { RiCheckboxCircleLine, RiErrorWarningLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import LoadingButton from '../ui/LoadingButton';
import { formatDate } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import rentalService, {
  CancellationFinalizeStatus,
  CancellationFinalizeResult,
} from '../../services/rentalService';

interface FinalizeCancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: CancellationFinalizeStatus | null;
  onFinalized: (result: CancellationFinalizeResult) => void;
  /** BUG-S2236-002: server ปฏิเสธ (เช่น อีกแท็บเปิดใบตามเก็บใหม่ระหว่างโมดัลเปิดค้าง) → ให้ caller
   *  โหลดสถานะใหม่ ไม่งั้นแบนเนอร์/โมดัลยังอ้าง "รับคืนครบแล้ว" จากข้อมูลเก่าและชวนกดซ้ำ */
  onRejected?: () => void;
}

const FinalizeCancellationModal: React.FC<FinalizeCancellationModalProps> = ({
  isOpen,
  onClose,
  status,
  onFinalized,
  onRejected,
}) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const guard = useSubmitGuard();

  // เปิดใหม่ทุกครั้งต้องเริ่มจากค่าว่าง — ไม่ให้หมายเหตุ/ข้อผิดพลาดของรอบก่อนติดค้างมาโผล่รอบนี้
  useEffect(() => {
    if (isOpen) {
      setNote('');
      setErrorMsg(null);
    }
  }, [isOpen]);

  // ยังไม่มีสถานะให้แสดง (caller ยังโหลดไม่เสร็จ/ยังไม่มีอะไรจะเสนอ) — ไม่ render อะไรเลย
  if (!status) return null;

  // ยังไม่ส่งมอบ = ของไม่เคยออกจากคลัง — หัวข้อ "รับคืนครบแล้ว" จะเป็นเท็จ (E2E 20 ก.ย. 2026 เคส C)
  const notDelivered = status.reason_code === 'ELIGIBLE_NOT_DELIVERED';
  // BUG-S2259-002: เดิมประกาศ "พร้อมใช้กับงานใหม่ได้ทันที" ทุกครั้ง แม้เพิ่งบันทึกชำรุด/สูญหาย
  const notUsable = status.not_usable ?? [];
  const notUsableText = notUsable.map((p) => `${p.label} (${p.status_label})`).join(', ');
  const lost = status.lost ?? [];
  const depositCount = status.deposit_to_refund?.count ?? 0;
  const depositAmount = status.deposit_to_refund?.amount ?? 0;

  const handleConfirm = async () => {
    if (!status.can_finalize) return;
    if (!guard.begin()) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const result = await rentalService.finalizeCancellation(status.rental_id, note.trim() || undefined);
      onFinalized(result);
    } catch (error: any) {
      // 400/403/404 ของ ก-2 ล้วนเป็นข้อความไทยพร้อมแสดง — โชว์ในโมดัลนี้แล้วเปิดค้างไว้ให้แก้ไข/ปิดเอง
      setErrorMsg(getApiErrorMessage(error, 'ไม่สามารถปิดใบแจ้งยกเลิกได้'));
      onRejected?.();
    } finally {
      setSubmitting(false);
      guard.end();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        // BUG-S2236-002: สถานะถูกโหลดใหม่หลัง server ปฏิเสธ (ของกลับไม่ครบแล้ว) — หัวข้อห้ามยังอ้าง "ครบแล้ว"
        !status.eligible
          ? 'ยังปิดใบแจ้งยกเลิกไม่ได้'
          : notDelivered
          ? 'ยังไม่ได้ส่งมอบของ — ปิดใบแจ้งยกเลิกเลยไหม?'
          // ของหาย ≠ รับคืน — หัวข้อห้ามอ้างว่า "รับคืนครบแล้ว"
          : lost.length > 0
          ? 'มีของหาย — ปิดใบแจ้งยกเลิกเลยไหม?'
          : 'รับคืนอุปกรณ์ครบแล้ว — ปิดใบแจ้งยกเลิกเลยไหม?'
      }
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
          >
            ไว้ทีหลัง
          </button>
          <LoadingButton
            type="button"
            loading={submitting}
            disabled={!status.can_finalize}
            onClick={handleConfirm}
          >
            ปิดใบแจ้งยกเลิก
          </LoadingButton>
        </div>
      }
    >
      <div className="space-y-4">
        {status.eligible ? (
          <div className="flex gap-2 p-3 text-sm rounded-lg bg-success-50 text-success-800">
            <RiCheckboxCircleLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>{status.message}</span>
          </div>
        ) : (
          // BUG-S2236-002: หน้าโหลดสถานะใหม่หลังถูกปฏิเสธ (และอาจ remount จนข้อความ error หาย) —
          // เหตุผลจาก server ต้องยังอยู่ในโมดัลเป็นคำเตือน ไม่ใช่กล่องสีเขียว + รายการ "จะเกิดอะไรขึ้น"
          <div role="alert" className="flex gap-2 p-3 text-sm rounded-lg bg-warning-50 text-warning-800">
            <RiErrorWarningLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>{status.message}</span>
          </div>
        )}

        {status.eligible && (
        <ul className="pl-1 space-y-1.5 text-sm list-disc list-inside text-secondary-700">
          <li>
            สัญญา <strong className="text-secondary-900">{status.rental_number || `#${status.rental_id}`}</strong>{' '}
            จะเปลี่ยนเป็น "ยกเลิกแล้ว" ทันที
          </li>
          <li>
            ยังคิดค่าเช่าถึง{' '}
            <strong className="text-secondary-900">
              {status.billing_end_date ? formatDate(status.billing_end_date) : '-'}
            </strong>{' '}
            ตามที่แจ้งไว้
          </li>
          <li>
            {notDelivered
              ? 'อุปกรณ์/ซิมที่จองไว้กลับเข้าคลัง พร้อมใช้กับงานใหม่ได้ทันที'
              : status.returned_count === 0
                ? 'ไม่มีชิ้นที่รับคืน — ไม่มีอะไรกลับเข้าคลัง'
                : notUsable.length === 0
                ? 'อุปกรณ์/ซิมที่รับคืนแล้วพร้อมใช้กับงานใหม่ได้ทันที'
                : notUsable.length >= status.returned_count
                  ? `ไม่มีชิ้นไหนกลับเข้าคลัง: ${notUsableText} — ต้องจัดการต่อ (ส่งซ่อม/ตัดจำหน่าย) ก่อนใช้กับงานใหม่`
                  : `ชิ้นที่รับคืนสภาพดีพร้อมใช้กับงานใหม่ได้ทันที · ยกเว้น ${notUsableText} ที่ไม่กลับเข้าคลัง`}
          </li>
          {lost.length > 0 && (
            <li data-testid="finalize-lost-pieces">
              บันทึกเป็นของหาย <strong className="text-secondary-900">{lost.length} ชิ้น</strong>: {lost.join(', ')} —
              ไม่กลับเข้าคลัง และไม่ออกใบตามเก็บใหม่
            </li>
          )}
          {depositCount > 0 && (
            <li>
              มัดจำ <strong className="text-secondary-900">฿{depositAmount.toLocaleString()}</strong> (
              {depositCount} รายการ) จะเข้าคิว "รอคืนมัดจำ" และเริ่มนับกำหนดคืน 15 วันทำการตั้งแต่วันนี้
            </li>
          )}
        </ul>
        )}

        {/* มติ 29 ต.ค. 2026: ปิดได้แม้มีของหาย แต่ต้องเตือนให้ฝ่ายบัญชีตรวจเรื่องหักมัดจำก่อนคืน */}
        {status.eligible && lost.length > 0 && depositCount > 0 && (
          <div role="alert" data-testid="finalize-lost-deposit-warning" className="flex gap-2 p-3 text-sm rounded-lg bg-warning-50 text-warning-800">
            <RiErrorWarningLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>มีของหาย — แจ้งฝ่ายบัญชีตรวจเรื่องหักมัดจำ/เรียกเก็บค่าของหายก่อนคืนมัดจำให้ลูกค้า</span>
          </div>
        )}

        {!status.can_finalize && status.blocked_reason && (
          <div className="flex gap-2 p-3 text-sm rounded-lg bg-warning-50 text-warning-800">
            <RiErrorWarningLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>{status.blocked_reason}</span>
          </div>
        )}

        {status.can_finalize && (
          <div>
            <label htmlFor="finalize-cancellation-note" className="block mb-1 text-sm font-medium text-secondary-900">
              หมายเหตุ <span className="text-xs font-normal text-secondary-500">(ไม่บังคับ)</span>
            </label>
            <textarea
              id="finalize-cancellation-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="เช่น รับของครบที่หน้าโครงการแล้ว"
              className="w-full px-3 py-2 text-sm border rounded-lg border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}

        {/* สถานะที่โหลดใหม่แสดงเหตุผลเดียวกันอยู่แล้ว → ไม่พิมพ์ซ้ำ (BUG-S2241-001) */}
        {errorMsg && !(!status.eligible && status.message === errorMsg) && (
          <div role="alert" className="flex gap-2 p-3 text-sm rounded-lg bg-danger-50 text-danger-800">
            <RiErrorWarningLine className="flex-shrink-0 w-5 h-5 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FinalizeCancellationModal;
